import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import './Psychotest.css';
import './Exam.css';

import useProctoring from '../hooks/useProctoring';
import ProctoringCamera from '../components/ProctoringCamera';

export default function Psychotest() {
  const navigate = useNavigate();
  const webcamRef = useRef(null);

  // =========================================================
  // STATE
  // =========================================================

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [psychotestStarted, setPsychotestStarted] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [rulesConfirmed, setRulesConfirmed] = useState(false);

  const [durationMinutes, setDurationMinutes] = useState(30);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [totalQuestionsSetting, setTotalQuestionsSetting] = useState(0);
  const [maxViolations, setMaxViolations] = useState(5);

  const [disqualified, setDisqualified] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [showMobileNav, setShowMobileNav] = useState(false);

  // =========================================================
  // REFS
  // =========================================================

  const timerRef = useRef(null);
  const disqualifySentRef = useRef(false);
  const autoSubmittedRef = useRef(false);

  // =========================================================
  // DEVICE CHECK
  // =========================================================

  const isMobileDevice = useMemo(() => {
    const ua = navigator.userAgent || '';
    const mobileUA = /Android|iPhone|iPad|iPod|Windows Phone|Mobile/i.test(ua);
    const coarsePointer =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(pointer: coarse)').matches
        : false;
    const smallScreen = typeof window !== 'undefined' ? window.innerWidth <= 1024 : false;
    return mobileUA || (coarsePointer && smallScreen);
  }, []);

  const requireFullscreen = !isMobileDevice;

  // =========================================================
  // PROCTORING (camera, face/object detection, fullscreen, tab guard, violations)
  // =========================================================

  const {
    faceStatus,
    objectDetectorReady,
    isFullscreen,
    enterFullscreen,
    violationCount,
    setViolationCount,
    registerViolation,
  } = useProctoring({
    webcamRef,
    cameraError,
    active: psychotestStarted,
    disqualified,
    setDisqualified,
    maxViolations,
    isMobileDevice,
    requireFullscreen,
    violationEndpoint: '/psychotest/violation',
    violationIdField: 'attempt_id',
    violationIdValue: attemptId,
  });

  // =========================================================
  // WEBCAM CONFIG
  // =========================================================

  const webcamVideoConstraints = useMemo(
    () => ({
      facingMode: { ideal: 'user' },
      width: { ideal: 640 },
      height: { ideal: 480 },
    }),
    []
  );

  // =========================================================
  // CAMERA ERROR / READY HANDLERS
  // =========================================================

  const handleCameraError = useCallback(() => {
    console.error('Camera unavailable');
    setCameraError(true);
    setError(
      isMobileDevice
        ? 'Kamera depan wajib digunakan. Pastikan izin kamera diberikan dan pilih kamera depan pada perangkat Anda.'
        : 'Kamera tidak tersedia. Pastikan izin kamera diberikan.'
    );
  }, [isMobileDevice]);

  const handleUserMedia = useCallback(
    (stream) => {
      if (!stream) { handleCameraError(); return; }
      const track = stream.getVideoTracks?.()[0];
      if (!track) { handleCameraError(); return; }

      const settings = track.getSettings ? track.getSettings() : {};
      const facingMode = String(settings?.facingMode || '').toLowerCase();
      const label = String(track?.label || '').toLowerCase();

      if (isMobileDevice) {
        const looksBackCamera =
          facingMode === 'environment' ||
          label.includes('back') ||
          label.includes('rear') ||
          label.includes('environment');

        if (looksBackCamera) {
          stream.getTracks().forEach((t) => t.stop());
          setCameraError(true);
          setError('Kamera depan wajib digunakan. Silakan pilih front camera pada browser Anda.');
          return;
        }
      }

      setCameraError(false);
      setError((prev) => (prev?.includes('Kamera depan wajib') ? '' : prev));
    },
    [handleCameraError, isMobileDevice]
  );

  // =========================================================
  // LOAD PSYCHOTEST DATA
  // =========================================================

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [statusRes, questionRes] = await Promise.all([
          API.get('/psychotest/status'),
          API.get('/psychotest/questions'),
        ]);

        if (!mounted) return;

        if (statusRes.data?.completed) {
          navigate('/dashboard');
          return;
        }

        setQuestions(questionRes.data?.questions || []);
        setDurationMinutes(Number(questionRes.data?.duration_minutes || statusRes.data?.duration_minutes || 30));
        setTotalQuestionsSetting(Number(questionRes.data?.total_questions_setting || statusRes.data?.total_questions_setting || 0));
        setMaxViolations(Number(questionRes.data?.max_violations_setting || statusRes.data?.max_violations_setting || 5));
      } catch (err) {
        console.error('Load psychotest error:', err);
        if (!mounted) return;
        setError(err.response?.data?.message || 'Gagal memuat psikotest');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [navigate]);

  // =========================================================
  // ANSWER HELPERS
  // =========================================================

  const answeredCount = useMemo(
    () => Object.keys(answers).filter((key) => !!answers[key]).length,
    [answers]
  );

  const handlePick = useCallback((questionId, value) => {
  setAnswers((prev) => ({ ...prev, [questionId]: value }));

  if (!attemptId) return;

  API.post('/psychotest/answer', {
    attempt_id: attemptId,
    question_id: questionId,
    value,
  }).catch((error) => {
    console.error('Save psychotest answer error:', error);
  });
}, [attemptId]);

  // =========================================================
  // SAVE INITIAL FACE PHOTO
  // =========================================================

  const saveInitialPsychotestFacePhoto = useCallback(async (currentAttemptId) => {
    if (!currentAttemptId || !webcamRef.current?.getScreenshot) return;
    try {
      const screenshot = webcamRef.current.getScreenshot();
      if (!screenshot) return;
      await API.post('/psychotest/save-face', { attempt_id: currentAttemptId, screenshot });
    } catch (error) {
      console.error('Save psychotest face photo error:', error);
    }
  }, []);

  // =========================================================
  // DISQUALIFICATION → notify server
  // =========================================================

  useEffect(() => {
    if (!disqualified || !psychotestStarted) return;
    if (disqualifySentRef.current) return;
    disqualifySentRef.current = true;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    API.post('/psychotest/disqualify', {
      violation_count: violationCount,
      reason: 'Melebihi batas maksimal pelanggaran',
    }).catch((error) => {
      console.error('Disqualify API error:', error);
    });
  }, [disqualified, psychotestStarted, violationCount]);

  // =========================================================
  // START PSYCHOTEST
  // =========================================================

  const startPsychotestFlow = async () => {
    if (requireFullscreen && !isFullscreen) {
      setError('Aktifkan mode layar penuh terlebih dahulu.');
      return;
    }
    if (cameraError) {
      setError('Kamera tidak tersedia. Pastikan izin kamera diberikan.');
      return;
    }
    if (faceStatus !== 'ok') {
      setError('Wajah belum terdeteksi dengan baik. Pastikan wajah terlihat jelas di kamera.');
      return;
    }
    if (!rulesConfirmed) {
      setError('Centang persetujuan peraturan sebelum mulai psikotest.');
      return;
    }

    try {
      const startRes = await API.post('/psychotest/start');
      const data = startRes.data || {};

      const serverRemaining = Number(data.remainingSeconds || 0);
      const serverDuration = Number(data.duration_minutes || durationMinutes || 30);
      const startedQuestions = Array.isArray(data.questions) ? data.questions : [];
      const serverTotalSetting = Number(data.total_questions_setting || totalQuestionsSetting || 0);
      const serverMaxViolations = Number(data.max_violations_setting || maxViolations || 5);
      const serverViolationCount = Number(data.violation_count || 0);
      const newAttemptId = data.attempt_id || null;

      setDurationMinutes(serverDuration);
      setTotalQuestionsSetting(serverTotalSetting);
      setMaxViolations(serverMaxViolations);
      setRemainingSeconds(serverRemaining > 0 ? serverRemaining : serverDuration * 60);
      setAttemptId(newAttemptId);
      if (startedQuestions.length > 0) setQuestions(startedQuestions);

      const restoredAnswers = {};
      const savedAnswers = data.answers && typeof data.answers === 'object' ? data.answers : {};
      Object.entries(savedAnswers).forEach(([key, value]) => {
        const questionId = Number(key);
        const answerValue = String(value || '').toLowerCase();
        if (questionId > 0 && (answerValue === 'a' || answerValue === 'b')) {
          restoredAnswers[questionId] = answerValue;
        }
      });

      const resumedQuestions = startedQuestions.length > 0 ? startedQuestions : questions;
      const firstUnansweredIndex = resumedQuestions.findIndex((q) => !restoredAnswers[q.id]);
      setCurrentIdx(
        firstUnansweredIndex >= 0
          ? firstUnansweredIndex
          : Math.max(0, resumedQuestions.length - 1)
      );
      setAnswers(restoredAnswers);
      setViolationCount(serverViolationCount);
      setDisqualified(false);
      disqualifySentRef.current = false;
      autoSubmittedRef.current = false;
      setError('');

      setPsychotestStarted(true);

      await saveInitialPsychotestFacePhoto(newAttemptId);
    } catch (error) {
      console.error('Start psychotest error:', error);
      setError(error.response?.data?.message || 'Gagal memulai psikotest');
    }
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (isAutoTimeout = false) => {
    if (!isAutoTimeout && answeredCount !== questions.length) {
      setError('Semua pertanyaan psikotest wajib diisi sebelum lanjut ke exam.');
      return;
    }

    if (isAutoTimeout && answeredCount !== questions.length) {
      setError('Waktu psikotest habis sebelum semua jawaban terisi. Silakan mulai ulang psikotest.');
      setSubmitting(false);
      setPsychotestStarted(false);
      setCurrentIdx(0);
      setAnswers({});
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        answers: questions.map((q) => ({
          question_id: q.id,
          value: String(answers[q.id]),
        })),
      };

      await API.post('/psychotest/submit', payload);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan hasil psikotest');
      setSubmitting(false);
    }
  };

  // =========================================================
  // TIMER
  // =========================================================

  useEffect(() => {
    if (!psychotestStarted || remainingSeconds <= 0) return undefined;

    timerRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [psychotestStarted, remainingSeconds]);

  const formatTime = useCallback((seconds) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const minutes = Math.floor(safeSeconds / 60).toString().padStart(2, '0');
    const secs = (safeSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${secs}`;
  }, []);

  // =========================================================
  // AUTO TIMEOUT
  // =========================================================

  useEffect(() => {
    if (!psychotestStarted) return;
    if (remainingSeconds > 0) return;
    if (autoSubmittedRef.current) return;

    autoSubmittedRef.current = true;
    handleSubmit(true);
  }, [psychotestStarted, remainingSeconds]); // eslint-disable-line

  // =========================================================
  // DERIVED STATE
  // =========================================================

  const fullscreenReady = requireFullscreen ? isFullscreen : true;
  const canStartPsychotest = fullscreenReady && !cameraError && faceStatus === 'ok' && rulesConfirmed;
  const currentQ = questions[currentIdx];
  const progressPercent = questions.length > 0 ? ((currentIdx + 1) / questions.length) * 100 : 0;

  // =========================================================
  // RENDER: LOADING / DISQUALIFIED
  // =========================================================

  if (loading) {
    return <div>Memuat psikotest...</div>;
  }

  if (disqualified) {
    return (
      <div className="precheck-screen">
        <section className="precheck-card">
          <div style={{ textAlign: 'center', fontSize: '48px', marginBottom: '20px' }}>🚫</div>
          <h2>Anda Didiskualifikasi</h2>
          <p>Anda telah melakukan lebih dari {maxViolations} pelanggaran pada psikotest.</p>
          <p>Psikotest dinyatakan <strong>TIDAK VALID.</strong></p>
          <button onClick={() => navigate('/dashboard')} className="btn-submit">
            Kembali ke Dashboard
          </button>
        </section>
      </div>
    );
  }

  // =========================================================
  // RENDER: MAIN
  // =========================================================

  return (
    <div className="psy-page">
      {requireFullscreen && !isFullscreen && (
        <div className="fullscreen-prompt">
          <div className = "fs-card">
            <strong className = 'text-white'>⛶ Mode Layar Penuh Diperlukan</strong>
            <p>Psikotest harus dijalankan dalam mode layar penuh.</p>
            <button className="btn-submit" onClick={enterFullscreen}>
              Aktifkan Layar Penuh
            </button>
          </div>
        </div>
      )}

      {error && <div className="psy-error">{error}</div>}

      {!psychotestStarted ? (
        <div className="precheck-screen">
          <section className="precheck-card">
            <h2 className='text-white'>Persiapan Sebelum Mulai Psikotest</h2>
            <p className="precheck-subtitle">
              Syarat pengerjaan psikotest sama seperti exam. Pastikan semua status sudah siap.
            </p>

            <div className="precheck-grid">
              <div className="precheck-item">
                <span className="check-dot ok"></span>
                <div>
                  <strong>Durasi Psikotest</strong>
                  <p>{durationMinutes} menit</p>
                </div>
              </div>

              <div className="precheck-item">
                <span className="check-dot ok"></span>
                <div>
                  <strong>Jumlah Soal Ditampilkan</strong>
                  <p>{totalQuestionsSetting > 0 ? `${totalQuestionsSetting} soal` : 'Semua soal aktif'}</p>
                </div>
              </div>

              <div className="precheck-item">
                <span className="check-dot ok"></span>
                <div>
                  <strong>Maksimal Pelanggaran</strong>
                  <p>{maxViolations} kali</p>
                </div>
              </div>

              <div className="precheck-item">
                <span className={`check-dot ${fullscreenReady ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Mode Layar Penuh</strong>
                  <p>{requireFullscreen ? (isFullscreen ? 'Aktif' : 'Belum aktif') : 'Mode mobile (opsional)'}</p>
                </div>
                {requireFullscreen && !isFullscreen && (
                  <button className="btn-mini" onClick={enterFullscreen}>Aktifkan</button>
                )}
              </div>

              <div className="precheck-item">
                <span className={`check-dot ${!cameraError ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Kamera</strong>
                  <p>{cameraError ? 'Kamera belum tersedia' : 'Kamera aktif'}</p>
                </div>
              </div>

              <div className="precheck-item">
                <span className={`check-dot ${faceStatus === 'ok' ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Deteksi Wajah</strong>
                  <p>
                    {faceStatus === 'ok'
                      ? 'Wajah terdeteksi'
                      : faceStatus === 'multiple'
                        ? 'Lebih dari satu wajah'
                        : 'Wajah belum terdeteksi'}
                  </p>
                </div>
              </div>
            </div>

            <div className="precheck-camera">
              <ProctoringCamera
                webcamRef={webcamRef}
                cameraError={cameraError}
                faceStatus={faceStatus}
                onUserMedia={handleUserMedia}
                onUserMediaError={handleCameraError}
                videoConstraints={webcamVideoConstraints}
                size="large"
              />
            </div>

            <div className="rules-box precheck-rules">
              <h3>⚠️ Peraturan Psikotest</h3>
              <ul>
                {requireFullscreen ? (
                  <li>Psikotest wajib dilakukan dalam mode layar penuh</li>
                ) : (
                  <li>Untuk perangkat mobile, mode layar penuh bersifat opsional</li>
                )}
                <li>Semua aktifitas akan direkam</li>
                <li>Dilarang berpindah tab atau keluar jendela browser</li>
                <li>Kamera wajah harus aktif selama psikotest</li>
                <li>Wajah harus terdeteksi selama psikotest berlangsung</li>
                <li>Copy-paste dinonaktifkan selama psikotest</li>
                <li>Dilarang membuka aplikasi lain selama psikotest</li>
                <li>Pastikan hanya satu wajah yang terlihat di kamera</li>
                <li>Pastikan pencahayaan ruangan cukup agar wajah tetap terdeteksi</li>
                <li>Pilih jawaban sesuai kondisi diri Anda</li>
              </ul>
            </div>

            <label className={`confirm-rule ${rulesConfirmed ? 'confirm-rule--checked' : 'confirm-rule--unchecked'}`}>
              <input
                type="checkbox"
                checked={rulesConfirmed}
                onChange={(event) => setRulesConfirmed(event.target.checked)}
              />
              Saya sudah membaca dan menyetujui seluruh peraturan psikotest.
            </label>

            <div className="precheck-example">
              <strong>Contoh Soal A/B:</strong>
              <p>a. Saya adalah pekerja keras</p>
              <p>b. Saya tidak mudah murung</p>
              <small>Pilih pernyataan yang paling menggambarkan diri Anda.</small>
            </div>

            <button
              className="btn-submit start-btn"
              onClick={startPsychotestFlow}
              disabled={!canStartPsychotest}
            >
              Mulai Kerjakan Psikotest
            </button>
          </section>
        </div>
      ) : (
        <>
          <header className="psy-exam-header">
            <div className="psy-exam-title">Psikotest Kepribadian</div>
            <div className="psy-exam-meta">
              {!requireFullscreen && <span className="violation-badge mobile-mode">📱 Mode Mobile</span>}
              <span className={`timer ${remainingSeconds <= 300 ? 'danger' : ''}`}>
                ⏱ {formatTime(remainingSeconds)}
              </span>
              <span className="progress-text">{answeredCount}/{questions.length} dijawab</span>
              <span className={`violation-badge text-white ${violationCount > 0 ? 'warn' : ''}`}>
                ⚠️ {violationCount}/{maxViolations}
              </span>
              <span className={`violation-badge text-white ${faceStatus !== 'ok' ? 'warn' : ''}`}>
                {faceStatus === 'ok'
                  ? '✅ Wajah Terdeteksi'
                  : faceStatus === 'multiple'
                    ? '⚠️ Multiple Face'
                    : '⚠️ Wajah Tidak Terdeteksi'}
              </span>
            </div>
          </header>

          <div className="psy-progress-bar">
            <div className="psy-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>

{isMobileDevice && psychotestStarted && (
  <div style={{
    padding: '8px 12px',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  }}>
    {/* Baris atas: kamera kecil + tombol toggle nav */}
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
    }}>
      {/* Kamera kecil */}
      <div style={{ width: '100px', height: '75px', overflow: 'hidden', borderRadius: '8px', flexShrink: 0 }}>
        <ProctoringCamera
          webcamRef={webcamRef}
          cameraError={cameraError}
          faceStatus={faceStatus}
          onUserMedia={handleUserMedia}
          onUserMediaError={handleCameraError}
          videoConstraints={webcamVideoConstraints}
          size="small"
        />
      </div>

      {/* Tombol toggle navigasi soal */}
      <button
        onClick={() => setShowMobileNav((prev) => !prev)}
        style={{
          flex: 1,
          padding: '10px',
          backgroundColor: showMobileNav ? '#0d7a7a' : '#e2e8f0',
          color: showMobileNav ? '#fff' : '#374151',
          border: 'none',
          borderRadius: '8px',
          fontWeight: '600',
          fontSize: '13px',
          cursor: 'pointer',
        }}
      >
        {showMobileNav ? '▲ Sembunyikan Soal' : '▼ Navigasi Soal'}
      </button>
    </div>

    {/* Nav soal — show/hide */}
    {showMobileNav && (
      <div style={{
        marginTop: '8px',
        display: 'flex',
        gap: '6px',
        flexWrap: 'wrap',
        maxHeight: '160px',
        overflowY: 'auto',
        padding: '4px 0',
      }}>
        {questions.map((q, i) => (
          <button
            key={q.id}
            className={`psy-q-btn ${i === currentIdx ? 'active' : ''} ${
              String(answers[q.id] || '').trim() !== '' ? 'answered' : ''
            }`}
            onClick={() => {
              setCurrentIdx(i);
              setShowMobileNav(false); 
            }}
            style={{
              minWidth: '36px',
              height: '36px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              flexShrink: 0,
            }}
            aria-label={`Soal ${i + 1}${String(answers[q.id] || '').trim() !== '' ? ', sudah dijawab' : ', belum dijawab'}`}
          >
            {i + 1}
          </button>
        ))}
      </div>
    )}
  </div>
)}

          <div className="psy-exam-body psy-layout">
  <aside className="psy-q-nav psy-sidebar">
    <h3>Navigasi Soal</h3>

    {!isMobileDevice && (
      <div className="psy-camera-box camera-box">
        <ProctoringCamera
          webcamRef={webcamRef}
          cameraError={cameraError}
          faceStatus={faceStatus}
          onUserMedia={handleUserMedia}
          onUserMediaError={handleCameraError}
          videoConstraints={webcamVideoConstraints}
          size="small"
        />
      </div>
    )}

    <div className="psy-legend">
      <span className="psy-leg answered"></span>Dijawab
      <span className="psy-leg active"></span>Aktif
      <span className="psy-leg empty"></span>Belum
    </div>

    <div className="psy-q-grid q-grid">
      {questions.map((question, index) => (
        <button
          key={question.id}
          className={`psy-q-btn ${index === currentIdx ? 'active' : ''} ${
            answers[question.id] ? 'answered' : ''
          }`}
          onClick={() => setCurrentIdx(index)}
        >
          {index + 1}
        </button>
      ))}
    </div>
  </aside>

          <main className="psy-main">
            <section className="psy-q-main">
              {currentQ && (
                <div className="psy-question-card">
                  <div className="psy-q-number">Soal {currentIdx + 1} dari {questions.length}</div>
                  <p className="psy-q-text">Pernyataan mana yang lebih menggambarkan Anda?</p>

                  <div className="psy-forced-options">
                    <button
                      type="button"
                      className={`psy-option-btn ${String(answers[currentQ.id] || '') === 'a' ? 'selected' : ''}`}
                      onClick={() => handlePick(currentQ.id, 'a')}
                    >
                      <span >A.</span>
                      <span >{currentQ.statement_a}</span>
                    </button>

                    <button
                      type="button"
                      className={`psy-option-btn ${String(answers[currentQ.id] || '') === 'b' ? 'selected' : ''}`}
                      onClick={() => handlePick(currentQ.id, 'b')}
                    >
                      <span>B.</span>
                      <span>{currentQ.statement_b}</span>
                    </button>
                  </div>

                  <div className="psy-nav-btns">
                    <button
                      className="btn-nav"
                      onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                      disabled={currentIdx === 0}
                    >
                      ← Sebelumnya
                    </button>

                    {currentIdx < questions.length - 1 ? (
                      <button
                        className="btn-nav btn-next"
                        onClick={() => setCurrentIdx((i) => Math.min(questions.length - 1, i + 1))}
                      >
                        Berikutnya →
                      </button>
                    ) : (
                      <button
                        className="btn-submit"
                        onClick={handleSubmit}
                        disabled={submitting || answeredCount !== questions.length}
                      >
                        {submitting ? 'Menyimpan...' : 'Selesaikan Psikotest dan Lanjut ke Exam'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </section>
          </main>
          </div>
        </>
      )}
    </div>
  );
}